import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { MySubscriptionDto, SubscriptionPlanMeta } from "common/models/types";
import {
  fetchMySubscription,
  fetchSubscriptionPlans,
} from "api/subscriptionService";

interface SubscriptionState {
  me: MySubscriptionDto | null;
  plans: SubscriptionPlanMeta[];
  isLoading: boolean;
  hasLoadedOnce: boolean;
  isReconciling: boolean;
  error: string | null;
}

const initialState: SubscriptionState = {
  me: null,
  plans: [],
  isLoading: false,
  hasLoadedOnce: false,
  isReconciling: false,
  error: null,
};

export const loadMySubscription = createAsyncThunk(
  "subscription/loadMe",
  async () => {
    const res = await fetchMySubscription();
    if (res.error || !res.data) {
      throw new Error(res.message || "구독 상태 조회 실패");
    }
    return res.data;
  }
);

export const loadSubscriptionPlans = createAsyncThunk(
  "subscription/loadPlans",
  async () => {
    const res = await fetchSubscriptionPlans();
    if (res.error) throw new Error(res.message || "요금제 조회 실패");
    return res.data || [];
  }
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setReconciling(state, action: PayloadAction<boolean>) {
      state.isReconciling = action.payload;
    },
    resetSubscription(state) {
      state.me = null;
      state.hasLoadedOnce = false;
      state.isReconciling = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMySubscription.pending, (s) => {
        s.isLoading = true;
      })
      .addCase(loadMySubscription.fulfilled, (s, a) => {
        s.isLoading = false;
        s.hasLoadedOnce = true;
        s.me = a.payload;
        s.error = null;
        if (a.payload.status !== "PENDING") s.isReconciling = false;
      })
      .addCase(loadMySubscription.rejected, (s, a) => {
        s.isLoading = false;
        s.hasLoadedOnce = true;
        s.error = a.error.message || null;
      })
      .addCase(loadSubscriptionPlans.fulfilled, (s, a) => {
        s.plans = a.payload;
      });
  },
});

export const { setReconciling, resetSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
